/**
 * Reine Logik der öffentlichen Unterschriftsseite `#/vorankuendigung/<token>`
 * (docs/vorankuendigung-unterschrift-plan.md §6, §6.1).
 *
 * Kein React, kein Supabase, kein DOM: Route, Zustände aus den Antworten von
 * `ladeVorankPublic` und der Route `api/vorank-unterschrift.ts`, die Karten je
 * Punkt, Texte, Prüfung der Felder und die Regeln der Abfrage-Uhr (QR am
 * Rechner). Die Komponente (VorankUnterschriftSeite.tsx) und der Hook
 * (useVorankUnterschrift.ts) bleiben dünn — es gibt kein jsdom im Test.
 */

import type { VorankErlaeuterung } from '../../../data/vorankErlaeuterungen';
import { NICHT_ANGEGEBEN } from '../../../services/pdf/fehlendeAngabe';
import {
    berlinIsoTag,
    istKalenderTag,
    istUnterschriftsBild,
    tagAusIso,
} from '../../../services/vorank/vorankAnfragePruefung';
import { berlinZeitpunkt } from '../../../services/vorank/vorankMail';
import type { VorankPublicLaden } from '../../../services/vorank/vorankUnterschriftPublicService';
import {
    VORANK_FUNKTION_MAX,
    VORANK_KORREKTUR_MAX,
    VORANK_LINK_TAGE,
    VORANK_NAME_MAX,
    VORANK_NAMENSTEIL_MAX,
    VORANK_TOKEN_MUSTER,
    type VorankPdfUnterschrift,
    type VorankPublicAntwort,
    type VorankRouteAntwort,
    type VorankUnterschrift,
    type VorankUnterschriftArt,
} from '../../../services/vorank/vorankUnterschriftTypes';
import {
    contactLines,
    describeRole,
    filledCoordinators,
    filledParties,
    filledPersons,
    normalizeVorankuendigung,
} from '../../../services/vorankuendigungModel';
import type { VorankuendigungCoordinator, VorankuendigungData } from '../../../types';

// ─── Route ───────────────────────────────────────────────────────────────────

/**
 * Jede Zeichenfolge aus Buchstaben, Ziffern, `-` und `_` nimmt die Route an:
 * ein verstümmelter Link soll „nicht gültig“ zeigen, nicht die Anmeldemaske.
 * Ob der Token taugt, entscheidet `tokenGueltig`.
 */
const ROUTE = /^#\/vorankuendigung\/([A-Za-z0-9_-]+)\/?(?:\?.*)?$/;

/** Token aus `#/vorankuendigung/<token>` (index.tsx); null = keine Unterschriftsseite. */
export const vorankRouteToken = (hash: string | null | undefined): string | null =>
    ROUTE.exec(hash ?? '')?.[1] ?? null;

/** Abgetippte Links kommen auch groß geschrieben an; die Datenbank vergibt nur Kleinbuchstaben. */
export const normalisiereToken = (roh: string | null | undefined): string => (roh ?? '').trim().toLowerCase();

export const tokenGueltig = (token: string): boolean => VORANK_TOKEN_MUSTER.test(token);

/** Letzte 6 Zeichen des Tokens — die Kennung in der grauen Nachweiszeile der PDF. */
export const kennungAusToken = (token: string): string => normalisiereToken(token).slice(-6);

// ─── Gerät ───────────────────────────────────────────────────────────────────

export type VorankGeraet = 'handy' | 'rechner';

/** `matchMedia('(pointer: coarse)')` → Handy/Tablet unterschreibt direkt, der Rechner wählt einen Weg. */
export const geraetAusZeiger = (grob: boolean): VorankGeraet => (grob ? 'handy' : 'rechner');

/** Die drei Wege am Rechner, in dieser Reihenfolge angeboten (E5). */
export type RechnerWeg = 'qr' | 'maus' | 'bild';

export const RECHNER_WEGE: ReadonlyArray<{ weg: RechnerWeg; titel: string; text: string }> = [
    {
        weg: 'qr',
        titel: 'Mit dem Handy unterschreiben',
        text: 'QR-Code mit dem Handy scannen und dort mit dem Finger unterschreiben. Diese Seite wechselt danach von selbst.',
    },
    {
        weg: 'maus',
        titel: 'Mit der Maus unterschreiben',
        text: 'Im Feld mit gedrückter Maustaste unterschreiben.',
    },
    {
        weg: 'bild',
        titel: 'Unterschrift als Bild hochladen',
        text: 'Ein Foto oder Scan Ihrer Unterschrift als PNG oder JPG, höchstens 5 MB.',
    },
];

// ─── Zustände ────────────────────────────────────────────────────────────────

export type VorankLadeStand = 'laedt' | VorankPublicLaden;

/**
 * Was die Seite zeigt:
 *  - `laedt`
 *  - `ungueltig`      Token falsch oder unbekannt.
 *  - `fehler`         Netz/Server beim Laden — „Erneut versuchen“.
 *  - `abgelaufen`     Link älter als seine Gültigkeit.
 *  - `ersetzt`        es gibt eine neuere Fassung (E4).
 *  - `offen`          Formular: prüfen, unterschreiben oder Korrektur melden.
 *  - `unterschrieben` wer/wann + Download der unterschriebenen Vorankündigung.
 */
export type VorankSeite = 'laedt' | 'ungueltig' | 'fehler' | 'abgelaufen' | 'ersetzt' | 'offen' | 'unterschrieben';

const istObjekt = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

export const seitenZustand = (lade: VorankLadeStand): VorankSeite => {
    if (lade === 'laedt') return 'laedt';
    if (lade.art === 'ungueltig') return 'ungueltig';
    if (lade.art === 'fehler') return 'fehler';
    // Ein Inhalt, den die Seite nicht lesen kann, ist kein Blatt zum Unterschreiben.
    if (!istObjekt(lade.daten.inhalt)) return 'fehler';
    return lade.daten.status;
};

export const ladeDaten = (lade: VorankLadeStand): VorankPublicAntwort | null =>
    lade !== 'laedt' && lade.art === 'ok' ? lade.daten : null;

export interface EndAnsicht {
    titel: string;
    text: string;
    /** „Erneut versuchen“ anbieten (nur beim Ladefehler). */
    erneut: boolean;
}

export const TEXTE = {
    seitenTitel: 'Vorankündigung unterschreiben',
    laedt: 'Vorankündigung wird geladen…',
    einleitung:
        'Bitte prüfen Sie die Angaben der Vorankündigung. Stimmt alles, bestätigen Sie mit Ihrer Unterschrift; sonst melden Sie über „Korrektur nötig“, was geändert werden soll.',
    haekchen: 'Ich habe die Angaben geprüft',
    vornameLabel: 'Vorname',
    nachnameLabel: 'Nachname',
    datumLabel: 'Datum der Unterschrift',
    datumHinweis: 'Steht so unter „Ort, Datum“ der Vorankündigung.',
    funktionLabel: 'Ihre Funktion (freiwillig)',
    funktionPlatzhalter: 'z. B. Geschäftsführer, Bauherrenvertreter',
    erlaeuterung: 'Was steht hier?',
    pdfAnsehen: 'PDF ansehen',
    pdfOeffnen: 'PDF öffnen',
    pdfHerunterladen: 'PDF herunterladen',
    unterschreibenAbschnitt: 'Unterschrift',
    jetztUnterschreiben: 'Jetzt unterschreiben',
    padTitel: 'Unterschrift des Bauherrn',
    padHinweis: 'Bitte im Feld unterschreiben. Die Unterschrift steht danach auf der Vorankündigung.',
    unterschriftUebernehmen: 'Unterschrift übernehmen',
    unterschriftArbeitet: 'Unterschrift wird übernommen…',
    unterschriftLoeschen: 'Unterschrift löschen und neu unterschreiben',
    absenden: 'Vorankündigung unterschreiben',
    sendet: 'Wird übertragen…',
    andererWeg: 'Anderen Weg wählen',
    qrTitel: 'Mit dem Handy unterschreiben',
    qrText:
        'Scannen Sie den Code mit der Kamera Ihres Handys. Dort öffnet sich dieselbe Seite; Häkchen, Name, Datum und Unterschrift tragen Sie am Handy ein.',
    qrWartet: 'Diese Seite prüft alle paar Sekunden, ob unterschrieben wurde.',
    qrZeitUm: 'Die automatische Prüfung ist nach 15 Minuten beendet.',
    erneutPruefen: 'Erneut prüfen',
    bildWaehlen: 'Bild auswählen',
    bildArbeitet: 'Bild wird aufbereitet…',
    vorschauTitel: 'So steht die Unterschrift auf der Vorankündigung',
    vorschauLinie: 'Unterschrift des Bauherrn / Verantwortlichen',
    anderesBild: 'Anderes Bild wählen',
    korrekturKnopf: 'Korrektur nötig',
    korrekturTitel: 'Korrektur melden',
    korrekturText:
        'Beschreiben Sie, was an der Vorankündigung nicht stimmt. Der Absender passt sie an, und Sie erhalten eine neue Fassung.',
    korrekturTextLabel: 'Was soll korrigiert werden?',
    korrekturSenden: 'Korrektur senden',
    korrekturAbbrechen: 'Abbrechen',
    korrekturGemeldet: 'Korrektur gemeldet — Sie erhalten eine neue Fassung.',
    trotzdemUnterschreiben: 'Stimmen die Angaben doch, können Sie trotzdem unterschreiben.',
    signiertHerunterladen: 'Unterschriebene Vorankündigung herunterladen',
    fenster: 'Sie können dieses Fenster jetzt schließen.',
    erneutVersuchen: 'Erneut versuchen',
} as const;

/** Endansichten ohne Formular: Titel und Erklärung. null = keine Endansicht. */
export const endAnsicht = (lade: VorankLadeStand): EndAnsicht | null => {
    const z = seitenZustand(lade);
    switch (z) {
        case 'ungueltig':
            return {
                titel: 'Dieser Link ist nicht gültig',
                text: 'Den Link zur Vorankündigung gibt es nicht oder nicht mehr. Bitte wenden Sie sich an den Absender der Vorankündigung.',
                erneut: false,
            };
        case 'abgelaufen': {
            const d = ladeDaten(lade);
            const bis = berlinTag(d?.gueltigBis);
            return {
                titel: 'Dieser Link ist abgelaufen',
                text: `Ein Link zur Unterschrift gilt ${VORANK_LINK_TAGE} Tage${bis ? ` (dieser bis ${bis})` : ''}. Bitte fordern Sie beim Absender eine neue Fassung der Vorankündigung an.`,
                erneut: false,
            };
        }
        case 'ersetzt':
            return {
                titel: 'Es gibt eine neuere Fassung',
                text: 'Es gibt eine neuere Fassung — bitte den Link aus der neuen PDF verwenden.',
                erneut: false,
            };
        case 'fehler': {
            const text = lade !== 'laedt' && lade.art === 'fehler'
                ? lade.text
                : 'Die Vorankündigung ließ sich nicht lesen. Bitte wenden Sie sich an den Absender der Vorankündigung.';
            return { titel: 'Die Vorankündigung konnte nicht geladen werden', text, erneut: lade !== 'laedt' && lade.art === 'fehler' };
        }
        default:
            return null;
    }
};

// ─── Datum ───────────────────────────────────────────────────────────────────

const BERLIN_TAG = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric',
});

/** Zeitstempel → Berliner Kalendertag TT.MM.JJJJ; leer/ungültig → ''. */
export const berlinTag = (zeitpunkt: string | null | undefined): string => {
    if (!zeitpunkt) return '';
    const ms = Date.parse(zeitpunkt);
    return Number.isNaN(ms) ? '' : BERLIN_TAG.format(ms);
};

/** Zeitstempel → „28.10.2026, 14:03 Uhr“ (Berlin); leer/ungültig → ''. */
export const berlinZeit = (zeitpunkt: string | null | undefined): string => berlinZeitpunkt(zeitpunkt) ?? '';

// ─── Kopf ────────────────────────────────────────────────────────────────────

const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : typeof v === 'number' && Number.isFinite(v) ? String(v) : '');

const zeilenAus = (v: unknown, trenner: RegExp = /\n/): string[] =>
    text(v).split(trenner).map((z) => z.trim()).filter(Boolean);

/** Absender aus `inhalt.sender`: erste Zeile Name, dann Anschrift/E-Mail. */
export const absenderZeilen = (inhalt: VorankuendigungData): string[] => zeilenAus(inhalt.sender);

/** „6254 · Neubau Kita“ — null, wenn die Fassung keine Projektangaben trägt. */
export const projektZeile = (d: Pick<VorankPublicAntwort, 'projektNummer' | 'projektName'>): string | null => {
    const teile = [text(d.projektNummer), text(d.projektName)].filter(Boolean);
    return teile.length ? teile.join(' · ') : null;
};

/** „Fassung vom 28.10.2026 · Link gültig bis 26.01.2027“ (gültig nur, solange offen). */
export const fassungZeile = (d: Pick<VorankPublicAntwort, 'fassungVom' | 'gueltigBis' | 'status'>): string => {
    const teile: string[] = [];
    const vom = berlinTag(d.fassungVom);
    if (vom) teile.push(`Fassung vom ${vom}`);
    const bis = berlinTag(d.gueltigBis);
    if (d.status === 'offen' && bis) teile.push(`Link gültig bis ${bis}`);
    return teile.join(' · ');
};

// ─── Karten je Punkt ─────────────────────────────────────────────────────────

export interface PunktGruppe {
    /** Unterüberschrift („a) für die Planung der Ausführung“), null = keine. */
    titel: string | null;
    /** Je Eintrag (Person, Firma) ein Block Zeilen. */
    eintraege: string[][];
    /** Nichts eingetragen — die Seite zeigt dann „nicht angegeben“ (wie die PDF). */
    fehlt: boolean;
}

export interface PunktKarte {
    punkt: string;
    titel: string;
    gruppen: PunktGruppe[];
    /** Erläuterung für den Bauherrn (data/vorankErlaeuterungen.ts), aufklappbar. */
    erlaeuterung: string | null;
}

/** Beschriftung je Punkt, wie in der PDF (ohne Nummer und Doppelpunkt). */
export const PUNKT_TITEL: Readonly<Record<string, string>> = {
    '1': 'Bezeichnung und Ort der Baustelle',
    '2': 'Name und Anschrift des Bauherrn',
    '3': 'Art und Bezeichnung des Bau- und/oder Abrissvorhabens',
    '4': 'Verantwortlicher Dritter (§ 4 BaustellV)',
    '5': 'Koordinator/en (§ 3 BaustellV)',
    '6': 'Voraussichtlicher Beginn und geplantes Ende der Arbeiten',
    '7': 'Voraussichtliche Zahl der gleichzeitig auf der Baustelle Beschäftigten',
    '8': 'Voraussichtliche Zahl der Arbeitgebenden',
    '9': 'Voraussichtliche Zahl der Unternehmer/innen ohne Beschäftigte',
    '10': 'Bereits ausgewählte Arbeitgebende',
    '11': 'Besonders gefährliche Arbeiten (§ 2 Abs. 3 BaustellV)',
    '12': 'Verantwortliche Bauleitung',
};

const gruppe = (eintraege: string[][], titel: string | null = null): PunktGruppe => {
    const voll = eintraege.map((e) => e.filter((z) => z.trim())).filter((e) => e.length > 0);
    return { titel, eintraege: voll, fehlt: voll.length === 0 };
};

const kontakt = (e: { phone?: unknown; email?: unknown }): string[] =>
    contactLines({ phone: text(e.phone), email: text(e.email) });

const koordinatorEintraege = (liste: VorankuendigungCoordinator[]): string[][] =>
    liste.map((k) => [describeRole(text(k.role), text(k.name)), ...zeilenAus(k.company), ...kontakt(k)]);

/**
 * Die Punkte 1–11 (12 nur mit verantwortlicher Bauleitung) mit den Werten der
 * Fassung — dieselben Felder, die `generateVorankuendigungPDF` druckt.
 * Altstände hebt `normalizeVorankuendigung` vorher ins Listenformat.
 */
export const punktKarten = (roh: VorankuendigungData, erlaeuterungen: readonly VorankErlaeuterung[]): PunktKarte[] => {
    const d = normalizeVorankuendigung(roh);
    const erl = (punkt: string): string | null => erlaeuterungen.find((e) => e.punkt === punkt)?.text ?? null;
    const karte = (punkt: string, gruppen: PunktGruppe[]): PunktKarte => ({
        punkt,
        titel: PUNKT_TITEL[punkt],
        gruppen,
        erlaeuterung: erl(punkt),
    });
    const einfach = (wert: unknown): PunktGruppe[] => [gruppe([zeilenAus(wert)])];

    const bauherr = [text(d.clientName), ...zeilenAus(d.clientAddress, /,\s*|\n/), ...kontakt({ phone: d.clientPhone, email: d.clientEmail })];
    const personen = filledPersons(d.clientPersons).map((p) => [describeRole(text(p.role), text(p.name)), ...kontakt(p)]);
    const bauherrGruppen = [gruppe([bauherr])];
    if (personen.length > 0) bauherrGruppen.push(gruppe(personen, 'Ansprechpartner (natürliche Personen)'));
    if (bauherrGruppen[0].fehlt && personen.length > 0) bauherrGruppen.shift();

    const dritte = filledParties(d.responsibleThirdParties)
        .map((p) => [describeRole(text(p.role), text(p.name)), ...zeilenAus(p.address), ...kontakt(p)]);

    const beginn = text(d.startDate);
    const ende = text(d.endDate);
    const zeitraum = beginn || ende ? [`von ${beginn || NICHT_ANGEGEBEN} bis ${ende || NICHT_ANGEGEBEN}`] : [];

    const arbeitgeber = (Array.isArray(d.employersList) ? d.employersList : []).map(text).filter(Boolean);

    const gefaehrlich = d.hasDangerousWork === true
        ? ['ja', ...zeilenAus(d.dangerousWorkDescription)]
        : ['nein'];

    const karten = [
        karte('1', einfach(d.siteAddress)),
        karte('2', bauherrGruppen),
        karte('3', einfach(d.projectDescription)),
        karte('4', [gruppe(dritte)]),
        karte('5', [
            gruppe(koordinatorEintraege(filledCoordinators(d.planningCoordinators)), 'a) für die Planung der Ausführung'),
            gruppe(koordinatorEintraege(filledCoordinators(d.executionCoordinators)), 'b) für die Ausführung des Bauvorhabens'),
        ]),
        karte('6', [gruppe([zeitraum])]),
        karte('7', einfach(d.maxWorkers)),
        karte('8', einfach(d.numEmployers)),
        karte('9', einfach(d.numContractors)),
        karte('10', [gruppe([arbeitgeber])]),
        karte('11', [gruppe([gefaehrlich])]),
    ];
    if (d.hasSiteManager === true) karten.push(karte('12', einfach(d.siteManager)));
    return karten;
};

// ─── Eingabe ─────────────────────────────────────────────────────────────────

/** Name, Funktion: Steuerzeichen raus, Leerraum zusammengezogen — so gehen sie an die Route. */
export const bereinigeName = (roh: string | null | undefined): string =>
    (roh ?? '').replace(/\p{Cc}/gu, ' ').replace(/\p{Cf}/gu, '').replace(/\s+/g, ' ').trim();

/** Korrekturtext: Umbrüche bleiben, höchstens eine Leerzeile, ohne Rand. */
export const bereinigeText = (roh: string | null | undefined): string =>
    (roh ?? '')
        .replace(/\r\n?/g, '\n')
        .replace(/[^\S\n]+/g, ' ')
        .replace(/(?!\n)\p{Cc}/gu, '')
        .replace(/\p{Cf}/gu, '')
        .split('\n')
        .map((z) => z.trim())
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();

// ─── Vor- und Nachname (E6, Betreiber 29.10.) ───────────────────────────────

/** Die beiden Pflichtfelder; an Route und RPC geht `vollerName`. */
export interface NamensTeile {
    vorname: string;
    nachname: string;
}

/**
 * Ein Name aus einem Feld (`inhalt.signerName`) → Vor- und Nachname: das
 * letzte Wort ist der Nachname, der Rest der Vorname — ein Titel („Dr.“)
 * bleibt so im Vornamen. Leer → beide leer; ein Wort → nur Nachname.
 * Passt das Ergebnis nicht in die Felder (ein Teil über 60 Zeichen oder
 * zusammen über 120 — etwa ein Firmenname in `signerName`), bleiben beide
 * leer: lieber selbst eintragen als mit einem Längenfehler vorbelegt.
 */
export const teileName = (roh: string | null | undefined): NamensTeile => {
    const woerter = bereinigeName(roh).split(' ').filter(Boolean);
    if (woerter.length === 0) return { vorname: '', nachname: '' };
    const teile = { vorname: woerter.slice(0, -1).join(' '), nachname: woerter[woerter.length - 1] };
    const passt = teile.vorname.length <= VORANK_NAMENSTEIL_MAX
        && teile.nachname.length <= VORANK_NAMENSTEIL_MAX
        && woerter.join(' ').length <= VORANK_NAME_MAX;
    return passt ? teile : { vorname: '', nachname: '' };
};

/** „Vorname Nachname“ — beide bereinigt, genau ein Leerzeichen dazwischen. */
export const vollerName = (n: NamensTeile): string =>
    [bereinigeName(n.vorname), bereinigeName(n.nachname)].filter(Boolean).join(' ');

/** Vorgabe für Vor- und Nachname: der Unterzeichner aus der Vorankündigung. */
export const namensVorgabe = (daten: VorankPublicAntwort): NamensTeile => teileName(text(daten.inhalt?.signerName));

/** Was an Vor- und Nachname fehlt, in der Reihenfolge der Seite. */
const fehlendeNamensteile = (n: NamensTeile): string[] => {
    const fehlt: string[] = [];
    if (!bereinigeName(n.vorname)) fehlt.push('Ihr Vorname');
    if (!bereinigeName(n.nachname)) fehlt.push('Ihr Nachname');
    return fehlt;
};

/** Je höchstens 60 Zeichen, zusammen (mit Leerzeichen) höchstens 120 — die Grenze der RPC. */
const namensFehler = (n: NamensTeile): string | null => {
    if (bereinigeName(n.vorname).length > VORANK_NAMENSTEIL_MAX) {
        return `Der Vorname darf höchstens ${VORANK_NAMENSTEIL_MAX} Zeichen lang sein.`;
    }
    if (bereinigeName(n.nachname).length > VORANK_NAMENSTEIL_MAX) {
        return `Der Nachname darf höchstens ${VORANK_NAMENSTEIL_MAX} Zeichen lang sein.`;
    }
    if (vollerName(n).length > VORANK_NAME_MAX) {
        return `Vor- und Nachname dürfen zusammen höchstens ${VORANK_NAME_MAX} Zeichen lang sein.`;
    }
    return null;
};

// ─── Datum der Unterschrift (E6) ─────────────────────────────────────────────

/** Was `<input type="date">` erlaubt: `YYYY-MM-DD`, `min` null = ohne Untergrenze. */
export interface DatumGrenzen {
    min: string | null;
    max: string;
}

/**
 * Nicht vor dem Tag der Fassung, nicht nach heute — beides als Berliner
 * Kalendertag (`heute` aus `berlinIsoTag(new Date())`). Geht die Uhr des
 * Geräts nach, läge „heute“ vor dem Tag der Fassung; dann gilt dieser als
 * Obergrenze (die Datenbank prüft ohnehin genau).
 */
export const datumGrenzen = (fassungVom: string | null | undefined, heute: string): DatumGrenzen => {
    const min = berlinIsoTag(fassungVom) || null;
    return { min, max: min && min > heute ? min : heute };
};

/** Vorgabe im Datumsfeld: heute (bzw. die Obergrenze, siehe `datumGrenzen`). */
export const datumVorgabe = (g: DatumGrenzen): string => g.max;

/** Fehler zum eingetragenen Datum; ein leeres Feld meldet `fehlendeAngaben`. null = in Ordnung. */
export const datumFehler = (datum: string, g: DatumGrenzen): string | null => {
    if (!datum) return null;
    if (!istKalenderTag(datum)) return 'Bitte ein gültiges Datum eintragen.';
    if (datum > g.max) return 'Das Datum darf nicht in der Zukunft liegen.';
    if (g.min && datum < g.min) return `Das Datum darf nicht vor dem Tag der Fassung (${tagAusIso(g.min)}) liegen.`;
    return null;
};

/**
 * Das Datumsfeld samt der Vorbelegung, die die Seite zuletzt selbst
 * eingetragen hat — daran erkennt sie, ob der Nutzer das Datum geändert hat.
 */
export interface DatumFeld {
    /** Wert im Feld (`YYYY-MM-DD` oder leer). */
    wert: string;
    /** Zuletzt von der Seite vorbelegter Tag; '' = noch keiner. */
    vorbelegt: string;
}

export const DATUM_FELD_LEER: DatumFeld = { wert: '', vorbelegt: '' };

/**
 * Hat der Nutzer das Datum selbst geändert? Nur, wenn im Feld etwas anderes
 * steht als die eigene Vorbelegung. Leer zählt nicht als eigener Wert (das
 * Feld ist Pflicht, die Vorbelegung füllt es wieder).
 */
export const datumSelbstGeaendert = (f: DatumFeld): boolean => f.wert !== '' && f.wert !== f.vorbelegt;

/**
 * Das Datumsfeld zu den Grenzen des AKTUELLEN Tages — beim Laden, wenn der
 * Tab wieder sichtbar wird und unmittelbar vor dem Absenden (ein Tab kann
 * über Mitternacht offen bleiben):
 *  - nicht selbst geändert → folgt der Vorgabe (heute);
 *  - selbst geändert → bleibt, solange gültig; sonst die Vorgabe.
 * Die Vorbelegung merkt sich immer die aktuelle Vorgabe.
 */
export const datumFeldFuer = (f: DatumFeld, g: DatumGrenzen): DatumFeld => {
    const vorgabe = datumVorgabe(g);
    const behalten = datumSelbstGeaendert(f) && istKalenderTag(f.wert) && datumFehler(f.wert, g) === null;
    return { wert: behalten ? f.wert : vorgabe, vorbelegt: vorgabe };
};

/** Ändert `datumFeldFuer` etwas? — damit die Seite nur dann neu setzt. */
export const gleichesDatumFeld = (a: DatumFeld, b: DatumFeld): boolean => a.wert === b.wert && a.vorbelegt === b.vorbelegt;

// ─── Unterschreiben ──────────────────────────────────────────────────────────

export interface UnterschriftEingabe extends NamensTeile {
    gelesen: boolean;
    funktion: string;
    /** Eingetragenes Datum `YYYY-MM-DD` (E6). */
    datum: string;
    grenzen: DatumGrenzen;
    /** PNG-data-URL oder null. */
    bild: string | null;
}

/** Was vor dem Absenden noch fehlt, in der Reihenfolge der Seite. */
export const fehlendeAngaben = (e: UnterschriftEingabe): string[] => {
    const fehlt: string[] = [];
    if (!e.gelesen) fehlt.push(`das Häkchen „${TEXTE.haekchen}“`);
    fehlt.push(...fehlendeNamensteile(e));
    if (!e.datum) fehlt.push('das Datum');
    if (!e.bild) fehlt.push('Ihre Unterschrift');
    return fehlt;
};

/** Harte Fehler (Längen, Datum, Bild), dieselben Grenzen wie Route und RPC; null = in Ordnung. */
export const eingabeFehler = (e: UnterschriftEingabe): string | null => {
    const name = namensFehler(e);
    if (name) return name;
    if (bereinigeName(e.funktion).length > VORANK_FUNKTION_MAX) return `Die Funktion darf höchstens ${VORANK_FUNKTION_MAX} Zeichen lang sein.`;
    const datum = datumFehler(e.datum, e.grenzen);
    if (datum) return datum;
    if (e.bild && !istUnterschriftsBild(e.bild)) {
        return 'Die Unterschrift ließ sich nicht übernehmen. Bitte löschen und erneut unterschreiben.';
    }
    return null;
};

export const kannUnterschreiben = (e: UnterschriftEingabe): boolean =>
    fehlendeAngaben(e).length === 0 && eingabeFehler(e) === null;

/** „Es fehlen noch: Ihr Name und Ihre Unterschrift.“ — null, wenn nichts fehlt. */
export const fehltHinweis = (fehlt: readonly string[]): string | null => {
    if (fehlt.length === 0) return null;
    const liste = fehlt.length === 1 ? fehlt[0] : `${fehlt.slice(0, -1).join(', ')} und ${fehlt[fehlt.length - 1]}`;
    return `Es fehlen noch: ${liste}.`;
};

/** Korrekturmeldung (E2) — Vor- und Nachname wie beim Unterschreiben (E6). */
export interface KorrekturEingabe extends NamensTeile {
    text: string;
}

/** Zeichen, die im Korrekturtext noch frei sind (negativ = zu lang). */
export const korrekturRest = (roh: string): number => VORANK_KORREKTUR_MAX - bereinigeText(roh).length;

/** Hinweis oder Fehler zur Korrektur; null = kann gesendet werden. */
export const korrekturHinweis = (e: KorrekturEingabe): string | null => {
    const fehlt = fehlendeNamensteile(e);
    if (!bereinigeText(e.text)) fehlt.push('der Text');
    if (fehlt.length) return fehltHinweis(fehlt);
    const name = namensFehler(e);
    if (name) return name;
    if (korrekturRest(e.text) < 0) return `Der Text ist zu lang (höchstens ${VORANK_KORREKTUR_MAX} Zeichen).`;
    return null;
};

/** Hinweis über dem Formular, wenn eine Korrektur gemeldet ist (eigene oder frühere). */
export const korrekturGemeldetText = (eigene: boolean, gemeldetAm: string | null | undefined): string | null => {
    if (eigene) return TEXTE.korrekturGemeldet;
    const tag = berlinTag(gemeldetAm);
    return tag ? `Am ${tag} wurde eine Korrektur gemeldet — Sie erhalten eine neue Fassung.` : null;
};

// ─── Nach dem Absenden ───────────────────────────────────────────────────────

export type VorankAktion = 'unterschreiben' | 'korrektur';

export interface SendeFolge {
    /** Auf eine Endansicht wechseln; null = die Seite bleibt. */
    wechsel: 'ungueltig' | 'ersetzt' | 'abgelaufen' | null;
    /** Die Fassung neu laden (unterschrieben, auch auf einem anderen Gerät). */
    neuLaden: boolean;
    /** Die eigene Unterschrift wurde angenommen → „Vielen Dank“. */
    danke: boolean;
    korrekturGemeldet: boolean;
    /** Klartext unter dem Formular; null = keiner. */
    fehler: string | null;
}

const FOLGE_LEER: SendeFolge = { wechsel: null, neuLaden: false, danke: false, korrekturGemeldet: false, fehler: null };

/** Kein Kontakt zur Route (Wurf beim Senden). */
export const NETZ_FOLGE: SendeFolge = {
    ...FOLGE_LEER,
    fehler: 'Keine Verbindung. Bitte die Internetverbindung prüfen und es erneut versuchen.',
};

/**
 * Antwort der Route → was die Seite tut. Eingabe-, Bremsen- und
 * Serverfehler lassen das Formular stehen (erneut versuchbar); ungültig,
 * ersetzt und abgelaufen wechseln auf die Endansicht, weil ein zweiter
 * Versuch nichts ändert. „Unterschrieben“ (auch von einem anderen Gerät)
 * lädt die Fassung neu und zeigt sie unterschrieben.
 */
export const folgeNachSenden = (aktion: VorankAktion, a: VorankRouteAntwort): SendeFolge => {
    if (a.ok === true) {
        if (aktion === 'korrektur') return { ...FOLGE_LEER, korrekturGemeldet: true };
        return { ...FOLGE_LEER, neuLaden: true, danke: !a.bereits };
    }
    switch (a.fehler) {
        case 'status':
            if (a.status === 'ersetzt' || a.status === 'abgelaufen') return { ...FOLGE_LEER, wechsel: a.status };
            return {
                ...FOLGE_LEER,
                neuLaden: true,
                fehler: aktion === 'korrektur' && a.status === 'unterschrieben'
                    ? 'Die Vorankündigung ist inzwischen unterschrieben — eine Korrektur ist über diesen Link nicht mehr möglich.'
                    : null,
            };
        case 'ungueltig':
            return { ...FOLGE_LEER, wechsel: 'ungueltig' };
        case 'eingabe':
            return {
                ...FOLGE_LEER,
                fehler: a.text?.trim() || (aktion === 'korrektur'
                    ? 'Bitte Name und Text prüfen und erneut senden.'
                    : 'Bitte Name und Unterschrift prüfen und erneut absenden.'),
            };
        case 'zu_viele':
            return {
                ...FOLGE_LEER,
                fehler: a.text?.trim() || 'Zu viele Versuche in kurzer Zeit. Bitte in einer Viertelstunde erneut versuchen.',
            };
        case 'server':
        default:
            return {
                ...FOLGE_LEER,
                fehler: a.text?.trim() || 'Das hat gerade nicht geklappt. Bitte in einigen Minuten erneut versuchen.',
            };
    }
};

/** Was die Seite selbst über die soeben abgeschickte Unterschrift weiß. */
export interface LokaleUnterschrift {
    name: string;
    funktion: string;
    bild: string;
    art: VorankUnterschriftArt;
    /** ISO-Zeitpunkt des Absendens. */
    am: string;
    /** Eingetragenes Datum `YYYY-MM-DD` (E6). */
    datum: string;
}

/**
 * Ladestand nach dem Absenden. Bei „Vielen Dank“ zeigt die Seite sofort die
 * eigene Unterschrift (das Nachladen ersetzt sie durch den Stand des Servers
 * — scheitert es, bleibt der Download trotzdem möglich).
 */
export const ladeNachFolge = (
    lade: VorankLadeStand,
    folge: SendeFolge,
    lokal: LokaleUnterschrift | null,
): VorankLadeStand => {
    if (folge.wechsel === 'ungueltig') return { art: 'ungueltig' };
    if (lade === 'laedt' || lade.art !== 'ok') return lade;
    if (folge.wechsel) return { art: 'ok', daten: { ...lade.daten, status: folge.wechsel } };
    if (folge.danke && lokal) {
        return {
            art: 'ok',
            daten: {
                ...lade.daten,
                status: 'unterschrieben',
                unterschrift: {
                    weg: 'digital',
                    am: lokal.am,
                    name: lokal.name,
                    funktion: lokal.funktion || null,
                    bild: lokal.bild,
                    art: lokal.art,
                    datum: istKalenderTag(lokal.datum) ? lokal.datum : null,
                },
            },
        };
    }
    return lade;
};

/**
 * Nachladen nach dem Absenden: nur ein lesbarer Stand ersetzt den bisherigen
 * — und nach einer bestätigten Unterschrift nie einer, der wieder „offen“
 * sagt (veraltete Antwort): die Seite springt nicht zurück aufs Formular.
 */
export const nachgeladen = (alt: VorankLadeStand, neu: VorankPublicLaden): VorankLadeStand => {
    if (neu.art !== 'ok') return alt;
    if (seitenZustand(alt) === 'unterschrieben' && neu.daten.status === 'offen') return alt;
    return neu;
};

// ─── Abfrage-Uhr (QR am Rechner) ─────────────────────────────────────────────

/** Alle 5 s nachsehen, ob am Handy unterschrieben wurde … */
export const ABFRAGE_TAKT_MS = 5_000;
/** … höchstens 15 min lang, dann „Erneut prüfen“. */
export const ABFRAGE_DAUER_MS = 15 * 60_000;

/**
 * - `aus`      QR nicht offen (oder Formular nicht mehr offen).
 * - `abfragen` jetzt nachsehen.
 * - `warten`   Tab im Hintergrund — keine Abfrage, die Uhr läuft weiter.
 * - `zeit_um`  15 min vorbei — anhalten, Knopf „Erneut prüfen“.
 */
export type AbfrageSchritt = 'aus' | 'abfragen' | 'warten' | 'zeit_um';

export const abfrageSchritt = (a: { aktiv: boolean; sichtbar: boolean; startMs: number | null; jetztMs: number }): AbfrageSchritt => {
    if (!a.aktiv || a.startMs === null) return 'aus';
    if (a.jetztMs - a.startMs >= ABFRAGE_DAUER_MS) return 'zeit_um';
    return a.sichtbar ? 'abfragen' : 'warten';
};

/**
 * Übernimmt die Seite das Ergebnis einer Abfrage? Nur, wenn sich etwas
 * geändert hat, das das Formular beendet (unterschrieben, ersetzt,
 * abgelaufen, Link weg). Ein Netzfehler zwischendurch ändert nichts — die
 * nächste Abfrage versucht es wieder.
 */
export const abfrageUebernehmen = (e: VorankPublicLaden): boolean =>
    e.art === 'ungueltig' || (e.art === 'ok' && e.daten.status !== 'offen');

// ─── Unterschrieben ──────────────────────────────────────────────────────────

/** Die Unterschrift für `generateVorankuendigungPDF` — nur digital und mit lesbarem Bild. */
export const pdfUnterschriftAus = (daten: VorankPublicAntwort, token: string): VorankPdfUnterschrift | null => {
    const u = daten.unterschrift;
    if (!u || u.weg !== 'digital' || !u.bild || !istUnterschriftsBild(u.bild)) return null;
    return {
        bild: u.bild,
        name: (u.name ?? '').trim(),
        funktion: u.funktion ?? null,
        am: u.am,
        // E6: das eingetragene Datum für „Ort, Datum“; die Nachweiszeile nimmt `am`.
        datum: u.datum ?? null,
        art: u.art ?? 'gezeichnet',
        kennung: kennungAusToken(token),
        fassungVom: daten.fassungVom,
    };
};

/**
 * „am 29.10.2026, 14:03 Uhr“ — oder, wenn das eingetragene Datum (E6) vom
 * Berliner Tag des Eingangs abweicht, „am 27.10.2026 (eingegangen am
 * 29.10.2026, 14:03 Uhr)“. Ohne lesbaren Zeitpunkt ''.
 */
export const unterschriebenAmText = (u: Pick<VorankUnterschrift, 'am' | 'datum'>): string => {
    const wann = berlinZeit(u.am);
    if (!wann) return '';
    const eingetragen = istKalenderTag(u.datum) && u.datum !== berlinIsoTag(u.am) ? tagAusIso(u.datum) : '';
    return eingetragen ? `am ${eingetragen} (eingegangen am ${wann})` : `am ${wann}`;
};

export interface FertigAnsicht {
    titel: string;
    zeilen: string[];
    /** „Unterschriebene Vorankündigung herunterladen“ anbieten. */
    download: boolean;
}

export const fertigAnsicht = (daten: VorankPublicAntwort, token: string, danke: boolean): FertigAnsicht => {
    const u = daten.unterschrift;
    const zeilen: string[] = [];
    if (u?.weg === 'papier') {
        const tag = berlinTag(u.am);
        zeilen.push(`Unterschrieben auf Papier${u.name ? ` von ${u.name}` : ''}${tag ? `, eingegangen am ${tag}` : ''}.`);
    } else if (u) {
        const wer = u.name ? `${u.name}${u.funktion ? ` (${u.funktion})` : ''}` : '';
        const wann = unterschriebenAmText(u);
        zeilen.push(`Unterschrieben${wer ? ` von ${wer}` : ''}${wann ? ` ${wann}` : ''}.`);
        if (u.art === 'bild') zeilen.push('Die Unterschrift wurde als Bild hochgeladen.');
    }
    if (danke) zeilen.push('Der Absender der Vorankündigung wird automatisch benachrichtigt.');
    const download = pdfUnterschriftAus(daten, token) !== null;
    if (!download) zeilen.push('Die unterschriebene Fassung erhalten Sie beim Absender der Vorankündigung.');
    return {
        titel: danke ? 'Vielen Dank — die Vorankündigung ist unterschrieben' : 'Diese Vorankündigung ist bereits unterschrieben',
        zeilen,
        download,
    };
};

/** Dateiname der PDF: „Vorankuendigung_6254.pdf“ bzw. „…_unterschrieben.pdf“. */
export const vorankPdfDateiname = (daten: Pick<VorankPublicAntwort, 'projektNummer'>, unterschrieben: boolean): string => {
    const nr = text(daten.projektNummer).replace(/[^A-Za-z0-9_-]+/g, '_').replace(/^_+|_+$/g, '');
    return `Vorankuendigung${nr ? `_${nr}` : ''}${unterschrieben ? '_unterschrieben' : ''}.pdf`;
};
